import React, { useCallback, useState } from "react";
// react-router-dom
import { useLocation, useNavigate } from "react-router-dom";
// @Mui
import {
  Avatar,
  Box,
  ButtonBase,
  Card,
  CircularProgress,
  Container,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
// __apis__
import { signOutRequest } from "src/__apis__/auth";
// paths
import { PATH_AUTH, PATH_DASHBOARD } from "src/routes/paths";
// hooks
import useLocales from "src/hooks/useLocales";
// hooks
import useToaster from "src/hooks/useToaster";
//
import Iconify from "src/components/Iconify";

// ------------------------------------------------------------------------------------------------

function Header() {
  const theme = useTheme();

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { triggerToast } = useToaster();

  const [isSigningOut, setIsSigningOut] = useState(false);

  const { currentLang, onChangeLang } = useLocales();

  const NAV_LINKS = [
    { title: "Home", href: PATH_DASHBOARD.home, icon: "solar:home-2-broken" },
    {
      title: "Nutrition",
      href: PATH_DASHBOARD.nutrition,
      icon: "icon-park-outline:nutrition",
    },
    {
      title: "Workout",
      href: PATH_DASHBOARD.workout,
      icon: "solar:dumbbell-broken",
    },
    {
      title: "Chats",
      href: PATH_DASHBOARD.chats,
      icon: "solar:chat-round-dots-broken",
    },
  ];

  const handleSignOut = useCallback(async () => {
    setIsSigningOut(true);
    await signOutRequest()
      .then((response) => {
        window.location.href = PATH_AUTH.signIn;
        triggerToast(
          "success",
          "We Will Miss You, Make Sure To Come Again Tommorow With New Ideas."
        );
      })
      .catch((error) => {
        triggerToast(
          "error",
          "We had an issue trying to sign you out, please try again later or sign in again using private tab."
        );
      })
      .finally(() => {
        setIsSigningOut(false);
      });
  }, []);

  const glassMorphismEffect = [PATH_DASHBOARD.algorithmForm].includes(pathname);

  return (
    <Box
      sx={{
        bgcolor: "transparent",
        zIndex: 10,
        position: "relative",
        ...(glassMorphismEffect && {
          backgroundColor: "rgba(17, 25, 40, 0.55)",
          backdropFilter: "blur(7px) saturate(180%)",
          WebkitBackdropFilter: "blur(5px) saturate(180%)",
          border: "1px solid rgba(255, 255, 255, 0.125)",
          position: "fixed",
          top: 0,
          color: "grey.100",
          width: "100%",
        }),
      }}
    >
      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{}}
          flexWrap="wrap"
        >
          <Box
            component="img"
            src="/logo.png"
            sx={{ width: 100 }}
            alt="FitRithm"
          />

          <Stack direction="row" alignItems="center" gap={3}>
            <Card sx={{ overflow: "hidden" }}>
              <Stack direction="row" alignItems="center" gap={3}>
                {NAV_LINKS.map((navLink, index) => (
                  <Tooltip title={navLink.title}>
                    <Box
                      component={ButtonBase}
                      key={index}
                      onClick={() => navigate(navLink.href)}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "99%",
                        bgcolor: pathname === navLink.href && "primary.main",
                        p: 1,
                      }}
                    >
                      <Iconify
                        sx={{
                          width: 25,
                          height: 25,
                          color:
                            pathname === navLink.href
                              ? "grey.100"
                              : "text.primary",
                        }}
                        icon={navLink.icon}
                      />
                    </Box>
                  </Tooltip>
                ))}
              </Stack>
            </Card>
            <Tooltip title="Change Language">
              <Box
                component={ButtonBase}
                onClick={() =>
                  onChangeLang(currentLang.value === "ar" ? "en" : "ar")
                }
              >
                <Box
                  component="img"
                  src={`${
                    currentLang.value === "ar"
                      ? "/assets/en.svg"
                      : "/assets/sa.svg"
                  }`}
                  sx={{ borderRadius: 1, width: 40, cursor: "pointer" }}
                />
              </Box>
            </Tooltip>
            <Avatar src="https://plus.unsplash.com/premium_photo-1671656349218-5218444643d8?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MXx8YXZhdGFyfGVufDB8fDB8fHww" />
            <Tooltip title="Sign Out">
              <Stack
                component={ButtonBase}
                onClick={handleSignOut}
                disabled={isSigningOut}
                alignItems="center"
                justifyContent="center"
                sx={{
                  bgcolor: "primary.main",
                  p: 1,
                  borderRadius: "99%",
                }}
              >
                {isSigningOut ? (
                  <CircularProgress size={25} color="grey.100" />
                ) : (
                  <Iconify
                    sx={{ width: 25, height: 25, color: "grey.100" }}
                    icon="solar:login-3-broken"
                  />
                )}
              </Stack>
            </Tooltip>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}

export default Header;
